import { SkipLink } from "@kovernow/react-ui/a11y/skip-link";
import { cn } from "@kovernow/react-ui/lib/utils";
import { homeQueryKeys } from "@kovernow/flows/home";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Bell } from "lucide-react";
import { useEffect, useRef } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useServices, useSession } from "../core/app-context";
import { cleanUpAfterSession } from "../core/session-cleanup";
import { useT } from "../core/use-t";
import { Logo } from "./Logo";
import { DESTINATIONS } from "./nav";
import { OfflineBanner } from "./OfflineBanner";

/**
 * Authenticated shell (CAP-4): BottomBar below 1280px, Sidebar at 1280px and
 * wider (Tailwind `xl`). Skip link, landmarks, the bell in the TopBar and
 * the offline banner. The live announcer is mounted once above the router
 * (App.tsx Providers) so announcements survive the Public↔App shell swap.
 */
export function AppShell() {
  const t = useT();
  const { authenticated } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const services = useServices();

  // Session ended mid-visit (refresh failed / signed out elsewhere).
  useEffect(() => {
    if (!authenticated) {
      // The account's cached data, drafts and upload sign-in must not
      // outlive its session.
      queryClient.clear();
      cleanUpAfterSession(services);
      const returnTo = location.pathname + location.search;
      navigate(`/sign-in?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
    }
  }, [authenticated, location.pathname, location.search, navigate, queryClient, services]);

  // Unread badge: refreshed on every route change; a failed fetch leaves a plain bell.
  const { homeApi, format } = services;
  const unseen = useQuery({
    queryKey: homeQueryKeys.unseen(),
    queryFn: () => homeApi.unseenCount(),
    enabled: authenticated,
    retry: false,
  });
  const refetchUnseen = unseen.refetch;
  // The query's own mount fetch covers the first page; refetch only when the
  // pathname actually changes afterwards.
  const lastPath = useRef(location.pathname);
  useEffect(() => {
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    if (authenticated) void refetchUnseen();
  }, [location.pathname, authenticated, refetchUnseen]);
  const unread = unseen.isError ? 0 : (unseen.data ?? 0);

  const item = (isActive: boolean) =>
    cn(
      "flex min-h-11 items-center gap-3 rounded-md px-3 text-sm font-semibold",
      isActive ? "bg-accent text-accent-foreground" : "text-foreground hover:bg-muted",
    );

  return (
    <div className="flex min-h-dvh bg-background text-foreground">
      <SkipLink href="#main">{t("shell.skipToContent")}</SkipLink>

      <aside data-testid="sidebar" className="hidden w-64 shrink-0 flex-col gap-6 border-r bg-sidebar p-4 xl:flex">
        <Link to="/" aria-label={t("shell.logoHome")} className="inline-flex min-h-11 items-center px-3">
          <Logo />
        </Link>
        <nav aria-label={t("shell.primaryNav")}>
          <ul className="flex flex-col gap-1">
            {DESTINATIONS.map(({ to, labelKey, icon: Icon, end }) => (
              <li key={to}>
                <NavLink to={to} end={end} className={({ isActive }) => item(isActive)}>
                  <Icon aria-hidden="true" className="size-5" />
                  {t(labelKey)}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-10 flex min-h-14 items-center justify-between border-b bg-background px-4">
          <Link to="/" aria-label={t("shell.logoHome")} className="inline-flex min-h-11 items-center xl:invisible">
            <Logo />
          </Link>
          <Link
            to="/notifications"
            data-testid="bell"
            aria-label={unread > 0 ? t("shell.notificationsUnread", { count: unread }) : t("shell.notifications")}
            className="relative inline-flex size-11 items-center justify-center rounded-md hover:bg-muted"
          >
            <Bell aria-hidden="true" className="size-5" />
            {unread > 0 ? (
              <span
                aria-hidden="true"
                data-testid="unread-badge"
                className="absolute top-1 right-1 inline-flex min-w-5 items-center justify-center rounded-full bg-status-error px-1 text-xs font-semibold text-status-error-foreground"
              >
                {format.number(Math.min(unread, 99))}
                {unread > 99 ? "+" : null}
              </span>
            ) : null}
          </Link>
        </header>
        <OfflineBanner />
        <main id="main" tabIndex={-1} className="flex-1 pb-24 xl:pb-8">
          <Outlet />
        </main>
      </div>

      <nav
        data-testid="bottom-bar"
        aria-label={t("shell.primaryNav")}
        className="fixed inset-x-0 bottom-0 z-10 border-t bg-background xl:hidden"
      >
        <ul className="grid grid-cols-5">
          {DESTINATIONS.map(({ to, labelKey, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  cn(
                    "flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs font-semibold",
                    isActive ? "text-primary" : "text-muted-foreground",
                  )
                }
              >
                <Icon aria-hidden="true" className="size-5" />
                {t(labelKey)}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </div>
  );
}

/** Public pages (sign-in, sign-up placeholder): logo header, no nav. */
export function PublicShell() {
  const t = useT();
  return (
    <div className="flex min-h-dvh flex-col bg-background text-foreground">
      <SkipLink href="#main">{t("shell.skipToContent")}</SkipLink>
      <header className="flex min-h-14 items-center border-b px-4">
        <Logo />
      </header>
      <OfflineBanner />
      <main id="main" tabIndex={-1} className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}
